import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Plus, Trash2 } from "lucide-react";

const toRow = (v) => ({ id: v.id, strength: v.strength, price: (v.price_cents / 100).toFixed(2), inventory: v.inventory || 0 });
const blank = () => ({ id: undefined, strength: "", price: "", inventory: 0 });

// Create or edit a parent product and its selectable strengths (each with its own price and stock).
export const ProductEditor = ({ product, onClose, onSave }) => {
  const [name, setName] = useState(product?.name || "");
  const [category, setCategory] = useState(product?.category || "research");
  const [form, setForm] = useState(product ? product.form || "" : "Lyophilized powder");
  const [description, setDescription] = useState(product?.description || "");
  const [rows, setRows] = useState(product?.variants?.length ? product.variants.map(toRow) : [blank()]);
  const [saving, setSaving] = useState(false);

  const set = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  const submit = async (e) => {
    e.preventDefault();
    const variants = rows.map((r) => ({
      ...(r.id ? { id: r.id } : {}),
      strength: r.strength.trim(),
      price_cents: Math.round(parseFloat(r.price) * 100),
      inventory: r.inventory,
    }));
    setSaving(true);
    await onSave({ name: name.trim(), category, form: form.trim(), description: category === "skincare" ? description.trim() : "", variants });
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl bg-[#F4EBDD] max-h-[90vh] overflow-y-auto" data-testid="product-editor">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{product ? "Edit product" : "New product"}</DialogTitle>
          <DialogDescription>Photos are shared by every option. Each strength or size has its own price.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label>Category</Label>
            <div className="mt-2 flex gap-2" role="radiogroup">
              {[["research", "Research Catalog"], ["skincare", "Skin Booster Serums"]].map(([k, l]) => (
                <button key={k} type="button" role="radio" aria-checked={category === k} onClick={() => { setCategory(k); if (k === "skincare" && form === "Lyophilized powder") setForm(""); }} data-testid={`pe-category-${k}`}
                  className={`rounded-full px-4 py-2 text-sm border transition-colors ${category === k ? "bg-[#59394F] text-white border-[#59394F]" : "bg-white border-[#A8B59A]/40"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Name</Label><Input required value={name} onChange={(e) => setName(e.target.value)} className="mt-2 rounded-xl" data-testid="pe-name" /></div>
            <div><Label>Form{category === "skincare" ? " (optional)" : ""}</Label><Input required={category === "research"} value={form} onChange={(e) => setForm(e.target.value)} className="mt-2 rounded-xl" data-testid="pe-form" /></div>
          </div>
          {category === "skincare" && (
            <div>
              <Label>Short description (optional, cosmetic wording only)</Label>
              <Textarea maxLength={600} value={description} onChange={(e) => setDescription(e.target.value)} className="mt-2 rounded-xl" rows={3} data-testid="pe-description" />
            </div>
          )}
          <div>
            <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-[0.2em] text-[#5E6B51] mb-2">
              <span className="col-span-6">Strength / size (exact units)</span><span className="col-span-5">Price ($)</span>
            </div>
            {rows.map((r, i) => (
              <div key={r.id || `new-${i}`} className="grid grid-cols-12 gap-2 mb-2" data-testid={`pe-row-${i}`}>
                <Input required placeholder="e.g. 10mg or 5ml" value={r.strength} onChange={(e) => set(i, "strength", e.target.value)} className="col-span-6 rounded-xl" data-testid={`pe-strength-${i}`} />
                <Input required type="number" min="0" step="0.01" value={r.price} onChange={(e) => set(i, "price", e.target.value)} className="col-span-5 rounded-xl" data-testid={`pe-price-${i}`} />
                <button type="button" disabled={rows.length === 1} onClick={() => setRows(rows.filter((_, j) => j !== i))} className="col-span-1 text-[#A84E35] disabled:opacity-30" data-testid={`pe-remove-${i}`}><Trash2 size={14} /></button>
              </div>
            ))}
            <Button type="button" variant="outline" onClick={() => setRows([...rows, blank()])} className="rounded-xl mt-1" data-testid="pe-add-strength"><Plus size={14} className="mr-1" />Add strength or size</Button>
          </div>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-xl bg-[#59394F] hover:bg-[#432A3B] text-white" data-testid="pe-save">{saving ? "Saving…" : "Save product"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
